"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

interface PortalReplacerProps {
	children: React.ReactNode;
	selector: string;
	parent?: HTMLElement | null;
}

export function PortalReplacer({
	children,
	selector,
	parent,
}: PortalReplacerProps) {
	// State to hold the container for the portal
	const [container, setContainer] = useState<HTMLElement | null>(null);
	const [innerHtmlEmptied, setInnerHtmlEmptied] = useState(false);

	useEffect(() => {
		if (!parent) return;

		// Function to try and find the target element by selector and update container
		const checkForContainer = () => {
			const targetElement = parent.querySelector(selector);
			if (targetElement && targetElement.parentElement) {
				setContainer(targetElement.parentElement);
				return true;
			}
			return false;
		};

		// Check immediately in case the element is already there
		if (checkForContainer()) return;

		if (!container) {
			// Set up a MutationObserver to watch for future changes in parent's subtree
			const observer = new MutationObserver(() => {
				if (checkForContainer()) {
					observer.disconnect();
				}
			});
			observer.observe(parent, { childList: true, subtree: true });
			return () => observer.disconnect();
		}
	}, [parent, selector, container]);

	useEffect(() => {
		// Once the container is available, ensure it is cleared just once.
		if (container && !innerHtmlEmptied) {
			container.innerHTML = "";
			setInnerHtmlEmptied(true);
		}
	}, [container, innerHtmlEmptied]);

	// Only render the portal when both the container is available and cleared.
	if (!container || !innerHtmlEmptied) {
		return null;
	}

	return createPortal(children, container);
}
